import { clsx } from 'clsx';
import { Link as RouterLink } from 'react-router-dom';

import {
  INSIGHTS_COACH_GUIDE_LABEL,
  PULSE_CHECK_COACH_GUIDE_URL,
} from '@/constants';
import {
  Avatar,
  Button,
  Card,
  Chip,
  Icon,
  Link,
  Paper,
  Typography,
} from '@/ui';

import PendingDebriefAction from './PendingDebriefAction';
import Section from './Section';

import type {
  PulseReportDeltaByQuestionKey,
  PulseReportDeltaBySectionKey,
} from './deltaTypes';
import type { InsightsViewerRole } from '@/constants';
import type {
  PulseReportAnswer,
  PulseReportDebrief,
  PulseReportPageStatus,
  PulseReportPayload,
  PulseReportSection,
} from '@/types';
import type { FC } from 'react';

import { roundToOneDecimal } from '@/utils/pulseReportAdapters';

interface PulseReportPageProps {
  backTo: string;
  debriefError?: string;
  errorMessage?: string;
  isMarkingDebriefed?: boolean;
  onMarkDebriefed?: () => void;
  onRetry?: () => void;
  report?: PulseReportPayload | null;
  reports?: PulseReportPayload[] | null;
  status?: PulseReportPageStatus;
  viewerRole?: InsightsViewerRole;
}

const STATUS_TITLE: Record<Exclude<PulseReportPageStatus, 'ready'>, string> = {
  error: 'Something went wrong while loading this pulse report',
  loading: 'Loading pulse report',
  not_found: 'Pulse report not found',
  unauthorized: 'You do not have access to this pulse report',
};

const STATUS_BODY: Record<Exclude<PulseReportPageStatus, 'ready'>, string> = {
  error: 'Try again in a moment.',
  loading: 'Please wait while we load report details.',
  not_found: 'The report may have been removed or the link is invalid.',
  unauthorized: 'If you think this is a mistake, contact an admin.',
};

const formatDate = (
  submittedAt: string,
  options: Intl.DateTimeFormatOptions,
  separator: string,
) =>
  new Intl.DateTimeFormat('en-US', {
    ...options,
    timeZone: 'UTC',
  })
    .format(new Date(submittedAt))
    .replaceAll('/', separator);

const formatSubmittedAtChip = (submittedAt: string) => {
  const date = new Date(submittedAt);

  return [
    date.getUTCFullYear().toString(),
    String(date.getUTCMonth() + 1).padStart(2, '0'),
    String(date.getUTCDate()).padStart(2, '0'),
  ].join(' ');
};

const formatSubmittedAtSubtitle = (submittedAt: string) =>
  formatDate(
    submittedAt,
    {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    },
    '-',
  );

const COACH_ASSESSMENT_SECTION_KEY = 'coach_assessment';

const numericAnswerMetrics = (
  answer: PulseReportAnswer,
): { max: number; value: number } | null => {
  if (answer.kind === 'range10' || answer.kind === 'rating5') {
    return {
      max: answer.max,
      value: answer.value,
    };
  }
  return null;
};

const getSectionByKey = (
  report: PulseReportPayload,
  sectionKey: PulseReportSection['key'],
) => report.sections.find((section) => section.key === sectionKey);

const normalizeSectionContextValue = (value?: string) => {
  const normalizedValue = value?.trim();
  return normalizedValue || null;
};

const coachAssessmentDeltasComparable = (
  latestReport?: PulseReportPayload | null,
  previousReport?: PulseReportPayload | null,
) => {
  if (!latestReport || !previousReport) {
    return false;
  }

  const latestSection = getSectionByKey(
    latestReport,
    COACH_ASSESSMENT_SECTION_KEY,
  );
  const previousSection = getSectionByKey(
    previousReport,
    COACH_ASSESSMENT_SECTION_KEY,
  );
  const latestCoachName = normalizeSectionContextValue(
    latestSection?.coachNameAtSubmission,
  );
  const previousCoachName = normalizeSectionContextValue(
    previousSection?.coachNameAtSubmission,
  );

  return latestCoachName !== null && latestCoachName === previousCoachName;
};

const sortBySubmittedAtDesc = (
  left: PulseReportPayload,
  right: PulseReportPayload,
) =>
  new Date(right.submittedAt).valueOf() - new Date(left.submittedAt).valueOf();

const buildDeltaByQuestionKey = (
  latestReport?: PulseReportPayload | null,
  previousReport?: PulseReportPayload | null,
): PulseReportDeltaByQuestionKey => {
  if (!latestReport || !previousReport) {
    return {};
  }

  const showCoachAssessmentDeltas = coachAssessmentDeltasComparable(
    latestReport,
    previousReport,
  );
  const previousValueByQuestionKey = new Map<
    string,
    { max: number; value: number }
  >();

  previousReport.sections.forEach((section) => {
    if (
      section.key === COACH_ASSESSMENT_SECTION_KEY &&
      !showCoachAssessmentDeltas
    ) {
      return;
    }

    section.answers.forEach((answer) => {
      const previousMetrics = numericAnswerMetrics(answer);
      if (previousMetrics !== null) {
        previousValueByQuestionKey.set(answer.questionKey, previousMetrics);
      }
    });
  });

  const deltaByQuestionKey: PulseReportDeltaByQuestionKey = {};

  latestReport.sections.forEach((section) => {
    if (
      section.key === COACH_ASSESSMENT_SECTION_KEY &&
      !showCoachAssessmentDeltas
    ) {
      return;
    }

    section.answers.forEach((answer) => {
      const currentMetrics = numericAnswerMetrics(answer);
      if (currentMetrics === null) {
        return;
      }
      const previousMetrics = previousValueByQuestionKey.get(
        answer.questionKey,
      );
      if (previousMetrics === undefined) {
        return;
      }
      deltaByQuestionKey[answer.questionKey] = {
        delta: roundToOneDecimal(currentMetrics.value - previousMetrics.value),
        previousMax: previousMetrics.max,
        previousSubmittedAt: previousReport.submittedAt,
        previousValue: previousMetrics.value,
      };
    });
  });

  return deltaByQuestionKey;
};

const buildDeltaBySectionKey = (
  latestReport?: PulseReportPayload | null,
  previousReport?: PulseReportPayload | null,
): PulseReportDeltaBySectionKey => {
  if (!latestReport || !previousReport) {
    return {};
  }

  const showCoachAssessmentDeltas = coachAssessmentDeltasComparable(
    latestReport,
    previousReport,
  );
  const previousSectionByKey = new Map(
    previousReport.sections.map((section) => [section.key, section]),
  );
  const deltaBySectionKey: PulseReportDeltaBySectionKey = {};

  latestReport.sections.forEach((section) => {
    if (
      section.key === COACH_ASSESSMENT_SECTION_KEY &&
      !showCoachAssessmentDeltas
    ) {
      return;
    }

    const previousSection = previousSectionByKey.get(section.key);
    if (
      previousSection === undefined ||
      section.averageScore === undefined ||
      previousSection.averageScore === undefined
    ) {
      return;
    }

    deltaBySectionKey[section.key] = {
      delta: roundToOneDecimal(
        section.averageScore - previousSection.averageScore,
      ),
      previousMax: 10,
      previousSubmittedAt: previousReport.submittedAt,
      previousValue: previousSection.averageScore,
    };
  });

  return deltaBySectionKey;
};

const buildSurveySubtitle = (
  activeReport?: PulseReportPayload | null,
  previousReport?: PulseReportPayload | null,
) => {
  if (!activeReport) {
    return null;
  }

  if (!previousReport) {
    return `Submitted on ${formatSubmittedAtSubtitle(activeReport.submittedAt)}.`;
  }

  return `Submitted on ${formatSubmittedAtSubtitle(activeReport.submittedAt)}. For comparison, prior survey submitted on ${formatSubmittedAtSubtitle(previousReport.submittedAt)}.`;
};

const splitMemberName = (fullName: string) => {
  const [firstName = '', ...rest] = fullName.trim().split(/\s+/u);

  return {
    firstName,
    lastName: rest.join(' '),
  };
};

const DebriefStatusRow = ({
  actorAvatarUrl,
  actorLabel,
  actorName,
  avatarUrl,
  memberName,
  status,
}: {
  actorAvatarUrl?: string | null;
  actorLabel?: string;
  actorName?: string | null;
  avatarUrl?: string | null;
  memberName: string;
  status?: PulseReportDebrief['status'];
}) => {
  if (status !== 'debriefed') {
    return null;
  }

  const displayName = actorName ?? memberName;
  if (!displayName) {
    return null;
  }

  const avatarSource = actorAvatarUrl ?? avatarUrl;
  const { firstName, lastName } = splitMemberName(displayName);

  return (
    <div className="flex items-center gap-2">
      <Icon className="text-[1.25rem] text-sage-600" aria-hidden>
        check
      </Icon>
      <Typography className="text-sage-600" variant="body2">
        {actorLabel ?? 'Discussed with'}
      </Typography>
      <Avatar
        firstName={firstName}
        lastName={lastName}
        size="xs"
        src={avatarSource ?? undefined}
      />
      <Typography className="text-sage-600" variant="body2">
        {displayName}
      </Typography>
    </div>
  );
};

const PulseReportPage: FC<PulseReportPageProps> = ({
  backTo,
  debriefError,
  errorMessage,
  isMarkingDebriefed = false,
  onMarkDebriefed,
  onRetry,
  report,
  reports,
  status = 'ready',
  viewerRole,
}) => {
  let reportList: PulseReportPayload[] = [];
  if (reports?.length) {
    reportList = reports;
  } else if (report) {
    reportList = [report];
  }
  const sortedReports = [...reportList].sort(sortBySubmittedAtDesc);
  const activeReport = sortedReports[0] ?? null;
  const previousReport = sortedReports[1] ?? null;
  const isRepeatSurvey = sortedReports.length > 1;
  const surveySubtitle = buildSurveySubtitle(
    activeReport,
    isRepeatSurvey ? previousReport : null,
  );
  const deltaByQuestionKey = buildDeltaByQuestionKey(
    activeReport,
    previousReport,
  );
  const deltaBySectionKey = buildDeltaBySectionKey(
    activeReport,
    previousReport,
  );
  const debrief = activeReport?.debrief;
  const isMemberViewer = viewerRole === 'member';
  const isCoachLikeViewer = viewerRole === 'coach' || viewerRole === 'admin';
  const debriefActor = isMemberViewer
    ? debrief?.debriefedBy
    : {
        avatarUrl: activeReport?.member.avatarUrl ?? null,
        fullName: activeReport?.member.fullName ?? '',
      };
  const showPendingDebriefAction =
    isCoachLikeViewer &&
    debrief?.status === 'pending' &&
    debrief.canMarkDebriefed;

  const sections =
    activeReport?.sections
      .filter((section) => section.answers.length > 0)
      .sort((a, b) => a.order - b.order) ?? [];

  return (
    <div className="p-4 lg:p-6" data-testid="pulse-report-page">
      <div className="mx-auto flex w-full max-w-280 flex-col gap-4">
        <Link
          className="flex no-underline"
          color="text.primary"
          component={RouterLink}
          to={backTo}
        >
          <Icon>chevron_left</Icon>Insights
        </Link>
        <div
          className={clsx('flex flex-col gap-4', {
            'xl:grid xl:grid-cols-[minmax(0,1fr)_10rem] xl:items-start':
              showPendingDebriefAction,
          })}
          data-testid="pulse-report-layout"
        >
          <Paper
            className="rounded-2xl p-6 lg:p-8"
            data-testid="pulse-report-paper"
            elevation={3}
          >
            {status !== 'ready' ? (
              <>
                <Typography className="text-cement-600" variant="h5">
                  {STATUS_TITLE[status]}
                </Typography>
                <Typography className="mt-2 text-cement-400" variant="body1">
                  {status === 'error' && errorMessage
                    ? errorMessage
                    : STATUS_BODY[status]}
                </Typography>
                {status === 'error' && onRetry ? (
                  <Button
                    className="mt-4 w-fit"
                    variant="text"
                    onClick={onRetry}
                  >
                    Retry
                  </Button>
                ) : null}
              </>
            ) : null}

            {status === 'ready' && !activeReport ? (
              <>
                <Typography className="text-cement-600" variant="h5">
                  No pulse report available
                </Typography>
                <Typography className="mt-2 text-cement-400" variant="body1">
                  Pulse report data has not been added yet.
                </Typography>
              </>
            ) : null}

            {status === 'ready' && activeReport ? (
              <div className="flex flex-col gap-4">
                <div className="mx-6 flex flex-col gap-4">
                  <DebriefStatusRow
                    actorAvatarUrl={debriefActor?.avatarUrl}
                    actorLabel={
                      isMemberViewer ? 'Debriefed with' : 'Discussed with'
                    }
                    actorName={debriefActor?.fullName}
                    avatarUrl={activeReport.member.avatarUrl}
                    memberName={activeReport.member.fullName}
                    status={debrief?.status}
                  />
                  {debrief?.status === 'debriefed' ? (
                    <hr className="border-0 border-t border-solid border-cement-200" />
                  ) : null}
                  <div className="mt-6 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div>
                      <Typography className="text-cement-600" variant="h4">
                        {activeReport.member.fullName}&apos;s Pulse Check
                      </Typography>
                      {surveySubtitle ? (
                        <Typography
                          className="mt-1 text-cement-400"
                          variant="body2"
                        >
                          {surveySubtitle}
                        </Typography>
                      ) : null}
                    </div>
                    {isCoachLikeViewer ? (
                      <Button
                        color="primary"
                        endIcon={<Icon fontSize="small">open_in_new</Icon>}
                        href={PULSE_CHECK_COACH_GUIDE_URL}
                        rel="noopener noreferrer"
                        size="small"
                        target="_blank"
                        variant="outlined"
                      >
                        {INSIGHTS_COACH_GUIDE_LABEL}
                      </Button>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Chip
                      className="bg-transparent text-cement-600"
                      label={formatSubmittedAtChip(activeReport.submittedAt)}
                      size="small"
                      variant="outlined"
                    />
                    {isRepeatSurvey ? (
                      <Chip
                        className="bg-transparent text-cement-600"
                        icon={<Icon>refresh</Icon>}
                        label="Repeat Survey"
                        size="small"
                        variant="outlined"
                      />
                    ) : null}
                    {activeReport.member.isManager ? (
                      <Chip
                        className="bg-transparent text-cement-600"
                        label="Manager"
                        size="small"
                        variant="outlined"
                      />
                    ) : null}
                  </div>
                </div>

                <hr className="mx-6 border-0 border-t border-solid border-cement-200" />

                {sections.length ? (
                  <div className="flex flex-col gap-4">
                    {sections.map((section, index) => (
                      <div key={section.key} className="flex flex-col gap-4">
                        <Section
                          deltaByQuestionKey={deltaByQuestionKey}
                          section={section}
                          sectionDelta={deltaBySectionKey[section.key]}
                        />
                        {index < sections.length - 1 ? (
                          <hr className="mx-6 border-0 border-t border-solid border-cement-200" />
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : (
                  <Card className="rounded-xl border border-cement-300 bg-white p-8 shadow-none">
                    <Typography className="text-cement-600" variant="h5">
                      No pulse report answers available
                    </Typography>
                    <Typography
                      className="mt-2 text-cement-400"
                      variant="body1"
                    >
                      There are currently no displayable answers in this report.
                    </Typography>
                  </Card>
                )}
              </div>
            ) : null}
          </Paper>
          {showPendingDebriefAction ? (
            <div className="xl:sticky xl:top-6">
              <PendingDebriefAction
                debriefError={debriefError}
                isMarkingDebriefed={isMarkingDebriefed}
                onMarkDebriefed={onMarkDebriefed}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export type { PulseReportPageProps };
export default PulseReportPage;
